import { useCallback, useEffect, useMemo, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faChevronDown,
  faChevronRight,
  faEllipsisVertical,
  faPaperPlane,
  faPause,
  faPencil,
  faPlay,
  faSliders,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';

import notificationApi from '@/core/notifications/api/notification-api';
import {
  configDisplayValue,
  Contact,
  deviceLabelFor,
  isRestrictedByFilter,
  NotificationApplication,
  NotificationChannel,
  Subscription,
  Topic,
} from '@/core/notifications/model';
import { fieldHeaderLabel, leafLabel } from '@/core/notifications/model/tag-schema';
import {
  Loading,
  SectionHeader,
  AppModal,
  EyebrowLabel,
  MenuList,
} from '@/shared/components/export';
import NotificationRow, {
  NOTIFICATION_ROW_DIVIDER_STYLE,
} from '@/core/notifications/components/settings/notification-row';
import { useOpenWidget } from '@/core/widget-navigation/export';
import { pluralize } from '@/shared/util/export';
import SubscriptionWizardModal from '@/core/notifications/components/subscription-wizard/subscription-wizard-modal';
import QuickEditNamePopover from '@/core/notifications/components/settings/subscriptions/quick-edit/quick-edit-name-popover';
import QuickEditChannelsPopover from '@/core/notifications/components/settings/subscriptions/quick-edit/quick-edit-channels-popover';
import QuickEditFiltersPopover from '@/core/notifications/components/settings/subscriptions/quick-edit/quick-edit-filters-popover';

function StatusPill({ paused }: { paused: boolean }) {
  return (
    <span
      style={{
        fontSize: 12,
        borderRadius: 3,
        padding: '3px 8px',
        fontWeight: 600,
        background: paused
          ? 'rgba(var(--bs-warning-rgb), 0.15)'
          : 'rgba(var(--bs-success-rgb), 0.15)',
        color: paused ? 'var(--bs-warning)' : 'var(--bs-success)',
        flexShrink: 0,
        whiteSpace: 'nowrap',
      }}
    >
      {paused ? 'Paused' : 'Active'}
    </span>
  );
}

export default function SubscriptionList({
  topics,
  channels,
  applications,
}: {
  topics: Topic[];
  channels: NotificationChannel[];
  applications: NotificationApplication[];
}) {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingSubscription, setEditingSubscription] = useState<Subscription | null>(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [subscriptionToDelete, setSubscriptionToDelete] = useState<Subscription | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [openQuickEdit, setOpenQuickEdit] = useState<{
    subscriptionId: string;
    field: 'name' | 'channels' | 'filters';
  } | null>(null);
  const openWidget = useOpenWidget();

  const pushContacts = useMemo(() => contacts.filter(c => c.channelKey === 'push'), [contacts]);

  const loadSubscriptions = useCallback(() => {
    return notificationApi
      .getSubscriptions()
      .then(setSubscriptions)
      .catch(() => toast.error('Could not load alarms'));
  }, []);

  useEffect(() => {
    loadSubscriptions();
  }, [loadSubscriptions]);

  const loadContacts = useCallback(() => {
    return notificationApi
      .getContacts()
      .then(setContacts)
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  const handleTogglePause = async (subscription: Subscription) => {
    try {
      await notificationApi.updateSubscription(subscription.id, {
        paused: !subscription.paused,
      });
      toast.success(subscription.paused ? 'Alarm resumed' : 'Alarm paused');
      loadSubscriptions();
    } catch (e) {
      toast.error('Could not update the alarm');
    }
  };

  const handleDelete = async () => {
    if (!subscriptionToDelete) return;
    try {
      await notificationApi.deleteSubscription(subscriptionToDelete.id);
      toast.success('Alarm deleted');
      loadSubscriptions();
    } catch (e) {
      toast.error('Could not delete the alarm');
    } finally {
      setSubscriptionToDelete(null);
    }
  };

  const handleCreateClick = () => {
    setEditingSubscription(null);
    setShowFormModal(true);
  };

  const closeQuickEdit = () => setOpenQuickEdit(null);

  const handleQuickEditSaved = () => {
    setOpenQuickEdit(null);
    loadSubscriptions();
  };

  const openQuickEditFor = (subscription: Subscription, field: 'name' | 'channels' | 'filters') => {
    // Contacts are only fetched once on mount otherwise — a contact added or
    // verified since then (e.g. via the create/edit wizard, or a verification
    // link opened in another tab) would stay invisible here without this.
    loadContacts();
    setOpenQuickEdit({ subscriptionId: subscription.id, field });
  };

  const restrictingDevicesBySubscription = useMemo(() => {
    const map = new Map<string, Contact[]>();
    (subscriptions ?? []).forEach(s => {
      map.set(
        s.id,
        pushContacts.filter(c => isRestrictedByFilter(c.subscriptionFilter, s.id)),
      );
    });
    return map;
  }, [pushContacts, subscriptions]);

  if (!subscriptions)
    return (
      <div className="p-4">
        <Loading type="bar" />
      </div>
    );

  const createButton = (
    <OverlayTrigger
      placement="left"
      overlay={topics.length === 0 ? <Tooltip>No notification topics available</Tooltip> : <></>}
    >
      <span>
        <button
          className="btn btn-sm btn-outline-secondary"
          disabled={topics.length === 0}
          onClick={handleCreateClick}
        >
          + New alarm
        </button>
      </span>
    </OverlayTrigger>
  );

  return (
    <div>
      <SectionHeader title="Alarms" />

      <div
        className={`px-3 pb-2 mutedText`}
        style={{
          fontSize: 14,
        }}
      >
        Your personal notification alarms. Each one filters a topic and delivers matching
        notifications to your chosen channels.
      </div>

      <div className="px-3 pb-3 d-flex justify-content-end">{createButton}</div>

      <div
        className="ms-3 mb-3"
        style={{
          border: '1px solid var(--bs-primary-200)',
          borderRadius: 0,
        }}
      >
        <div
          className="d-flex align-items-center gap-2 px-3 py-2"
          style={{ background: 'var(--bs-primary-200)' }}
        >
          <FontAwesomeIcon icon={faBell} className="mutedText" style={{ width: 13 }} />
          <span
            className="mutedText"
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            Alarms
          </span>
        </div>

        {subscriptions.length === 0 && (
          <div className={`p-4 text-center mutedText`} style={{ fontSize: 14 }}>
            No alarms yet. Create one to start receiving notifications.
          </div>
        )}

        {subscriptions.map(subscription => {
          const isExpanded = expandedId === subscription.id;
          const topic = topics.find(t => t.key === subscription.topic);
          const tagSchema = topic?.tagSchema;
          const restrictingDevices = restrictingDevicesBySubscription.get(subscription.id) ?? [];
          const restrictingDeviceCount = restrictingDevices.length;
          const restrictingDeviceNames = restrictingDevices
            .map(c => deviceLabelFor(c) ?? c.identityValue)
            .join(', ');

          const actionButtons = (
            <div className="d-flex align-items-center gap-1 flex-shrink-0">
              <button
                className="btn btn-sm btn-outline-secondary"
                style={{ padding: '3px 8px', lineHeight: 1 }}
                onClick={() => handleTogglePause(subscription)}
                aria-label={subscription.paused ? 'Resume' : 'Pause'}
                title={subscription.paused ? 'Resume' : 'Pause'}
              >
                <FontAwesomeIcon icon={subscription.paused ? faPlay : faPause} />
              </button>
              <MenuList
                trigger={<FontAwesomeIcon icon={faEllipsisVertical} />}
                toggleClassName="btn btn-sm btn-outline-secondary"
                toggleTitle="More options"
                align="right"
                elevated
                items={[
                  {
                    key: 'rename',
                    label: 'Rename',
                    icon: faPencil,
                    onSelect: () => openQuickEditFor(subscription, 'name'),
                  },
                  {
                    key: 'channels',
                    label: 'Delivery channels',
                    icon: faPaperPlane,
                    onSelect: () => openQuickEditFor(subscription, 'channels'),
                  },
                  {
                    key: 'filters',
                    label: 'Filters',
                    icon: faSliders,
                    onSelect: () => openQuickEditFor(subscription, 'filters'),
                  },
                  { key: 'divider', label: '', divider: true },
                  {
                    key: 'delete',
                    label: 'Delete',
                    icon: faTrash,
                    danger: true,
                    onSelect: () => setSubscriptionToDelete(subscription),
                  },
                ]}
              />
            </div>
          );

          return (
            <div key={subscription.id}>
              <NotificationRow style={NOTIFICATION_ROW_DIVIDER_STYLE}>
                <div
                  className="w-100 d-flex flex-column flex-md-row align-items-md-center gap-1"
                  style={{ minWidth: 0 }}
                >
                  {/* Line 1: chevron + pill + name + topic (desktop) */}
                  <div
                    className="d-flex align-items-center gap-2 flex-grow-1"
                    style={{ minWidth: 0 }}
                  >
                    <button
                      className={`btn btn-sm btn-link p-0 flex-shrink-0 mutedText`}
                      style={{ width: 16 }}
                      onClick={() => setExpandedId(isExpanded ? null : subscription.id)}
                      aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    >
                      <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} />
                    </button>
                    <StatusPill paused={subscription.paused} />
                    {restrictingDeviceCount > 0 && (
                      <button
                        type="button"
                        onClick={() => openWidget('settings', 'notifications/contacts')}
                        title={`Restricted on: ${restrictingDeviceNames}. Manage in Contacts.`}
                        className="btn"
                        style={{
                          fontSize: 12,
                          borderRadius: 3,
                          padding: '3px 8px',
                          fontWeight: 600,
                          background: 'rgba(var(--bs-info-rgb), 0.15)',
                          color: 'var(--bs-info)',
                          flexShrink: 0,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Push limited on {restrictingDeviceCount}{' '}
                        {pluralize(restrictingDeviceCount, 'device')}
                      </button>
                    )}
                    <span
                      className="text-truncate flex-grow-1"
                      style={{
                        fontSize: 16,
                        fontWeight: 600,
                        minWidth: 0,
                      }}
                      title={subscription.name}
                    >
                      {subscription.name}
                    </span>
                    <span
                      className={`d-none d-md-block text-truncate flex-shrink-0 mutedText`}
                      style={{ fontSize: 14, maxWidth: 180 }}
                      title={topic?.displayName ?? subscription.topic}
                    >
                      {topic?.displayName ?? subscription.topic}
                    </span>
                    <div className="d-none d-md-flex">{actionButtons}</div>
                  </div>
                  {/* Line 2 (mobile only): topic + actions */}
                  <div className="d-flex d-md-none align-items-center justify-content-between gap-2 ps-4">
                    <span className={`text-truncate mutedText`} style={{ fontSize: 14 }}>
                      {topic?.displayName ?? subscription.topic}
                    </span>
                    {actionButtons}
                  </div>
                </div>
              </NotificationRow>

              {/* Expanded detail */}
              {isExpanded && (
                <div
                  className="px-4 py-3 border-bottom mutedText d-flex flex-column flex-md-row gap-4"
                  style={{
                    fontSize: 14,
                    background: 'rgba(var(--bs-primary-200-rgb), 0.3)',
                    ...NOTIFICATION_ROW_DIVIDER_STYLE,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <EyebrowLabel>Filtering by</EyebrowLabel>
                    {Object.keys(subscription.tagFilter).length === 0 ? (
                      <span>Everything for this topic — no filter applied.</span>
                    ) : (
                      <ul className="mb-0 ps-3">
                        {Object.entries(subscription.tagFilter).map(([key, value]) => {
                          const field = tagSchema?.[key];
                          const values = (Array.isArray(value) ? value : [value]).map(entry =>
                            leafLabel(field, String(entry)),
                          );
                          return (
                            <li key={key} title={field?.description}>
                              {fieldHeaderLabel(field, key)}: {values.join(', ')}
                              {field?.description && (
                                <div className="mutedText" style={{ fontSize: 12, marginTop: 4 }}>
                                  {field.description}
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <EyebrowLabel>Delivered to</EyebrowLabel>
                    {subscription.defaultChannels.length === 0 ? (
                      <span>No channels selected.</span>
                    ) : (
                      <ul className="mb-0 ps-3">
                        {subscription.defaultChannels.map(channelKey => {
                          const channel = channels.find(c => c.key === channelKey);
                          const override = subscription.channelOverrides[channelKey];
                          const isPushChannel = channelKey === 'push';
                          return (
                            <li key={channelKey}>
                              {channel?.displayName ?? channelKey}
                              {channel &&
                                override &&
                                Object.keys(override).length > 0 &&
                                ` → ${configDisplayValue(channel, override)}`}
                              {isPushChannel && restrictingDeviceCount > 0 && (
                                <span style={{ color: 'var(--bs-info)' }}>
                                  {' '}
                                  (limited on {restrictingDeviceCount}{' '}
                                  {pluralize(restrictingDeviceCount, 'device')})
                                </span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {openQuickEdit &&
        (() => {
          const quickEditSubscription = subscriptions.find(
            s => s.id === openQuickEdit.subscriptionId,
          );
          if (!quickEditSubscription) return null;

          if (openQuickEdit.field === 'name') {
            return (
              <QuickEditNamePopover
                subscription={quickEditSubscription}
                onClose={closeQuickEdit}
                onSaved={handleQuickEditSaved}
              />
            );
          }

          if (openQuickEdit.field === 'channels') {
            return (
              <QuickEditChannelsPopover
                subscription={quickEditSubscription}
                channels={channels}
                contacts={contacts}
                onClose={closeQuickEdit}
                onSaved={handleQuickEditSaved}
              />
            );
          }

          const quickEditTopic = topics.find(t => t.key === quickEditSubscription.topic);
          return (
            <QuickEditFiltersPopover
              subscription={quickEditSubscription}
              tagSchema={quickEditTopic?.tagSchema ?? {}}
              onClose={closeQuickEdit}
              onSaved={handleQuickEditSaved}
            />
          );
        })()}

      <AppModal
        show={!!subscriptionToDelete}
        onClose={() => setSubscriptionToDelete(null)}
        icon={faTrash}
        title="Confirm Deletion"
        primaryLabel="Confirm Delete"
        onPrimary={handleDelete}
        primaryDanger
      >
        Are you sure you want to delete the alarm <strong>{subscriptionToDelete?.name}</strong>?
        This action cannot be undone.
      </AppModal>

      {showFormModal && (
        <SubscriptionWizardModal
          existingSubscription={editingSubscription}
          topics={topics}
          applications={applications}
          channels={channels}
          onCancel={() => setShowFormModal(false)}
          onDone={() => {
            setShowFormModal(false);
            loadSubscriptions();
            // a contact may have been added inline during the wizard session
            loadContacts();
          }}
        />
      )}
    </div>
  );
}
